import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building, Download, CheckCircle, CreditCard, AlertCircle } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

// ── PayPal Live Client-ID ──────────────────────────────────────────────────
const PAYPAL_CLIENT_ID =
  'AVc094pScqjW_aodGLt7RRuvMh2CpigvWp08xiBBWXbJSnT3d-Aho3zyWqsCJNhMzZkLovmCZxPD0zQN';

// ── Updated bank details ───────────────────────────────────────────────────
const BANK = {
  iban:    'DE91120400000134626100',
  bic:     'COBADEFFXXX',
  holder:  'Klimaneutral Berlin 2030 GmbH',
  address: 'Stresemannstr. 23, D-10963 Berlin',
};

const CheckoutPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [orderDetails,    setOrderDetails]    = useState(null);
  const [paymentMethod,   setPaymentMethod]   = useState('bank-transfer');
  const [orderConfirmed,  setOrderConfirmed]  = useState(false);
  const [paypalError,     setPaypalError]     = useState(null);
  const [paypalSuccess,   setPaypalSuccess]   = useState(false);
  const [customerInfo,    setCustomerInfo]    = useState({
    firstName: '', lastName: '', email: '', phone: ''
  });
  const [customerErrors,  setCustomerErrors]  = useState({});

  useEffect(() => {
    const params     = new URLSearchParams(location.search);
    const totalTrees = params.get('trees');
    const totalPrice = params.get('price');

    if (totalTrees && totalPrice) {
      setOrderDetails({
        trees:   parseInt(totalTrees),
        price:   parseFloat(totalPrice),
        orderId: `CO2-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
      });
    }
  }, [location]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({ ...prev, [name]: value }));
    setCustomerErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateCustomerInfo = () => {
    const errors = {};
    if (!customerInfo.firstName.trim()) errors.firstName = true;
    if (!customerInfo.lastName.trim()) errors.lastName = true;
    if (!customerInfo.email.trim() || !/\S+@\S+\.\S+/.test(customerInfo.email)) errors.email = true;
    if (!customerInfo.phone.trim()) errors.phone = true;
    setCustomerErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirmOrder = () => {
    if (paymentMethod === 'bank-transfer' && !validateCustomerInfo()) return;

    // Save order to localStorage for admin panel
    try {
      const orders = JSON.parse(localStorage.getItem('checkoutOrders') || '[]');
      orders.unshift({
        ...orderDetails,
        paymentMethod,
        customerInfo: paymentMethod === 'bank-transfer' ? customerInfo : null,
        confirmedAt: new Date().toISOString(),
        status: 'pending'
      });
      localStorage.setItem('checkoutOrders', JSON.stringify(orders));
    } catch (err) {
      console.warn('Could not save order:', err);
    }

    setOrderConfirmed(true);
  };

  const downloadInvoice = () => {
    const content = `CO2 Neutral Page – Rechnung / Invoice
====================================================
Bestellnummer / Order ID : ${orderDetails?.orderId}
Datum / Date             : ${new Date().toLocaleDateString('de-DE')}

Positionen / Items:
  ${orderDetails?.trees} Bäume / Trees × €8,00 = €${orderDetails?.price?.toFixed(2)}

Gesamtbetrag / Total: €${orderDetails?.price?.toFixed(2)}

====================================================
Bankverbindung / Payment Details:
  IBAN           : ${BANK.iban}
  BIC            : ${BANK.bic}
  Kontoinhaber   : ${BANK.holder}
  Adresse        : ${BANK.address}
  Verwendungszweck / Reference: ${orderDetails?.orderId}
====================================================
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `rechnung-${orderDetails?.orderId}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── PayPal handlers ───────────────────────────────────────────────────────
  const createOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [
        {
          description: `CO₂-neutrale Zertifizierung – ${orderDetails?.trees} Bäume`,
          amount: {
            currency_code: 'EUR',
            value: orderDetails?.price?.toFixed(2),
          },
          custom_id: orderDetails?.orderId,
        },
      ],
      application_context: {
        brand_name:          'CO2 Neutral Page',
        locale:              currentLanguage === 'de' ? 'de-DE' : 'en-GB',
        user_action:         'PAY_NOW',
        shipping_preference: 'NO_SHIPPING',
      },
    });
  };

  const onApprove = (data, actions) => {
    return actions.order.capture().then((details) => {
      // Save PayPal order to localStorage
      try {
        const orders = JSON.parse(localStorage.getItem('checkoutOrders') || '[]');
        orders.unshift({
          ...orderDetails,
          paymentMethod: 'paypal',
          customerInfo: {
            firstName: details?.payer?.name?.given_name || '',
            lastName: details?.payer?.name?.surname || '',
            email: details?.payer?.email_address || '',
            phone: ''
          },
          paypalOrderId: data.orderID,
          confirmedAt: new Date().toISOString(),
          status: 'paid'
        });
        localStorage.setItem('checkoutOrders', JSON.stringify(orders));
      } catch (err) {
        console.warn('Could not save PayPal order:', err);
      }
      setPaypalSuccess(true);
      setOrderConfirmed(true);
    });
  };

  const onError = (err) => {
    console.error('PayPal Fehler:', err);
    setPaypalError(t('paypal-error') || 'PayPal-Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.');
  };

  // ── Guard: no order ───────────────────────────────────────────────────────
  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <h1 className="text-2xl font-bold text-green-800 mb-4">{t('checkout-no-order')}</h1>
            <button
              onClick={() => navigate(`/${currentLanguage}`)}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200"
            >
              {t('back-to-home')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Success screen ────────────────────────────────────────────────────────
  if (orderConfirmed) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-green-800 mb-4">{t('order-confirmed')}</h1>
            <p className="text-lg text-neutral-600 mb-6">{t('order-confirmed-desc')}</p>

            {/* Show bank details only if paid by bank transfer */}
            {!paypalSuccess && (
              <div className="bg-green-50 rounded-xl p-6 mb-6 text-left">
                <h2 className="text-xl font-semibold text-green-800 mb-4">{t('payment-instructions')}</h2>
                <div className="space-y-2 text-sm">
                  <p><strong>IBAN:</strong> {BANK.iban}</p>
                  <p><strong>BIC:</strong>  {BANK.bic}</p>
                  <p><strong>{t('account-holder')}:</strong> {BANK.holder}</p>
                  <p><strong>{t('address')}:</strong> {BANK.address}</p>
                  <p><strong>{t('reference')}:</strong> <span className="font-mono">{orderDetails.orderId}</span></p>
                </div>
              </div>
            )}

            {paypalSuccess && (
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  ✅ PayPal-Zahlung erfolgreich abgeschlossen. Vielen Dank!
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center flex-wrap">
              <button
                onClick={downloadInvoice}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 flex items-center gap-2"
              >
                <Download size={20} />
                {t('download-invoice')}
              </button>
              <button
                onClick={() => navigate(`/${currentLanguage}`)}
                className="bg-neutral-600 hover:bg-neutral-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200"
              >
                {t('back-to-home')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Checkout form ─────────────────────────────────────────────────────────
  return (
    <PayPalScriptProvider
      options={{
        'client-id': PAYPAL_CLIENT_ID,
        currency: 'EUR',
        intent: 'capture',
        locale: currentLanguage === 'de' ? 'de_DE' : 'en_GB',
      }}
    >
      <div className="min-h-screen bg-neutral-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h1 className="text-3xl font-bold text-green-800 mb-8 text-center">
              {t('checkout-title')}
            </h1>

            {/* ── Order Summary ────────────────────────────────────────── */}
            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-green-800 mb-4">{t('order-summary')}</h2>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg text-green-700">
                    {orderDetails.trees} {t('tree-calculator-trees')}
                  </p>
                  <p className="text-sm text-neutral-600">
                    {t('order-id')}: {orderDetails.orderId}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-800">
                    €{orderDetails.price.toFixed(2)}
                  </p>
                  <p className="text-sm text-neutral-600">{t('total-amount')}</p>
                </div>
              </div>
            </div>

            {/* ── Payment Method Selection ─────────────────────────────── */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-green-800 mb-4">{t('payment-method')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Bank Transfer */}
                <label
                  className={`flex items-center gap-3 cursor-pointer border-2 rounded-xl p-4 transition-all duration-200 ${
                    paymentMethod === 'bank-transfer'
                      ? 'border-green-500 bg-green-50'
                      : 'border-neutral-200 hover:border-green-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="bank-transfer"
                    checked={paymentMethod === 'bank-transfer'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="text-green-600 accent-green-600"
                  />
                  <Building className="text-green-600 shrink-0" size={22} />
                  <div>
                    <p className="font-medium text-neutral-800">{t('bank-transfer')}</p>
                    <p className="text-xs text-neutral-500">SEPA · kostenlos</p>
                  </div>
                </label>

                {/* PayPal */}
                <label
                  className={`flex items-center gap-3 cursor-pointer border-2 rounded-xl p-4 transition-all duration-200 ${
                    paymentMethod === 'paypal'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-neutral-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="paypal"
                    checked={paymentMethod === 'paypal'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="text-blue-600 accent-blue-600"
                  />
                  <CreditCard className="text-blue-600 shrink-0" size={22} />
                  <div>
                    <p className="font-medium text-neutral-800">PayPal</p>
                    <p className="text-xs text-neutral-500">Sofortzahlung · sicher</p>
                  </div>
                </label>
              </div>
            </div>

            {/* ── Bank Transfer Details ────────────────────────────────── */}
            {paymentMethod === 'bank-transfer' && (
              <>
                {/* Customer Info Form */}
                <div className="bg-white rounded-xl p-6 mb-6 border-2 border-green-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    {currentLanguage === 'de' ? 'Ihre Kontaktdaten' : currentLanguage === 'en' ? 'Your Contact Details' : 'İletişim Bilgileriniz'}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {currentLanguage === 'de' ? 'Vorname' : currentLanguage === 'en' ? 'First Name' : 'Ad'} *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={customerInfo.firstName}
                        onChange={handleCustomerInfoChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${customerErrors.firstName ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                        placeholder={currentLanguage === 'de' ? 'Max' : currentLanguage === 'en' ? 'John' : 'Ahmet'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {currentLanguage === 'de' ? 'Nachname' : currentLanguage === 'en' ? 'Last Name' : 'Soyad'} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={customerInfo.lastName}
                        onChange={handleCustomerInfoChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${customerErrors.lastName ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                        placeholder={currentLanguage === 'de' ? 'Mustermann' : currentLanguage === 'en' ? 'Doe' : 'Yılmaz'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {currentLanguage === 'de' ? 'E-Mail-Adresse' : currentLanguage === 'en' ? 'Email Address' : 'E-posta Adresi'} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={customerInfo.email}
                        onChange={handleCustomerInfoChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${customerErrors.email ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                        placeholder={currentLanguage === 'de' ? 'max@beispiel.de' : currentLanguage === 'en' ? 'john@example.com' : 'ahmet@ornek.com'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">
                        {currentLanguage === 'de' ? 'Telefonnummer' : currentLanguage === 'en' ? 'Phone Number' : 'Telefon Numarası'} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={customerInfo.phone}
                        onChange={handleCustomerInfoChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${customerErrors.phone ? 'border-red-400 bg-red-50' : 'border-neutral-300'}`}
                        placeholder={currentLanguage === 'de' ? '+49 1512 3456789' : currentLanguage === 'en' ? '+44 7123 456789' : '+90 555 123 4567'}
                      />
                    </div>
                  </div>
                  {Object.keys(customerErrors).length > 0 && (
                    <p className="mt-3 text-sm text-red-600">
                      {currentLanguage === 'de' ? '* Bitte füllen Sie alle Pflichtfelder aus.' : currentLanguage === 'en' ? '* Please fill in all required fields.' : '* Lütfen tüm zorunlu alanları doldurun.'}
                    </p>
                  )}
                </div>

                {/* Bank Details */}
                <div className="bg-neutral-50 rounded-xl p-6 mb-8 border border-neutral-200">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">{t('bank-details')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between gap-4 flex-wrap">
                      <span className="font-medium text-neutral-600">{t('iban')}:</span>
                      <span className="font-mono font-bold text-neutral-800 tracking-wide">
                        {BANK.iban}
                      </span>
                    </div>
                    <div className="flex justify-between gap-4 flex-wrap">
                      <span className="font-medium text-neutral-600">{t('bic')}:</span>
                      <span className="font-mono text-neutral-800">{BANK.bic}</span>
                    </div>
                    <div className="flex justify-between gap-4 flex-wrap">
                      <span className="font-medium text-neutral-600">{t('account-holder')}:</span>
                      <span className="text-neutral-800">{BANK.holder}</span>
                    </div>
                    <div className="flex justify-between gap-4 flex-wrap">
                      <span className="font-medium text-neutral-600">{t('address')}:</span>
                      <span className="text-neutral-800">{BANK.address}</span>
                    </div>
                    <div className="flex justify-between gap-4 flex-wrap border-t border-neutral-200 pt-3">
                      <span className="font-medium text-neutral-600">{t('reference')}:</span>
                      <span className="font-mono font-bold text-green-700">{orderDetails.orderId}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── PayPal Buttons ───────────────────────────────────────── */}
            {paymentMethod === 'paypal' && (
              <div className="mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">
                    PayPal – €{orderDetails.price.toFixed(2)} EUR
                  </h3>

                  {paypalError && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                      <p className="text-sm text-red-700">{paypalError}</p>
                    </div>
                  )}

                  <PayPalButtons
                    style={{
                      layout:  'vertical',
                      color:   'blue',
                      shape:   'rect',
                      label:   'paypal',
                      height:  48,
                    }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                    forceReRender={[orderDetails.price]}
                  />
                </div>
              </div>
            )}

            {/* ── Important Notes ──────────────────────────────────────── */}
            <div className="bg-yellow-50 rounded-xl p-6 mb-8 border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">{t('important-notes')}</h3>
              <ul className="space-y-2 text-sm text-yellow-700">
                <li>• {t('note-1')}</li>
                <li>• {t('note-2')}</li>
                <li>• {t('note-3')}</li>
              </ul>
            </div>

            {/* ── Confirm Button (bank transfer only) ──────────────────── */}
            {paymentMethod === 'bank-transfer' && (
              <div className="text-center">
                <button
                  onClick={handleConfirmOrder}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-200 shadow-lg"
                >
                  {t('confirm-order')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

export default CheckoutPage;
